import { z } from 'zod';
import { ResponseUnmaskStateSchema } from '~/features/shared/market-content/responseUnmask.schemas';
import { listingSubspecialtySchema } from '~/features/types';
import {
  JobResponseActionSchema,
  JobResponseAvailableActionsSchema,
  JobResponseTurnSchema,
} from '~/types/hlx/jobResponse';

export const B2BOpportunityStatusSchema = z.enum([
  'MESSAGED',
  'APPLIED',
  'NEGOTIATING',
  'PENDING_ACCEPTANCE',
  'ACCEPTED',
  'REJECTED',
  'WITHDRAWN',
]);

// [MCV-1889] Optional and nullable so an older payload (before the sell side) still parses.
const B2BListingSideSchema = z.enum(['SEEKING', 'OFFERING']).nullish();

// Which side of the deal the viewer is on, whatever the listing side: the buyer pays.
export const B2BViewerRoleSchema = z.enum(['BUYER', 'SUPPLIER']).nullish();

// Why a response is closed. 'LISTING_FILLED': another response on the listing became the contract.
// Kept a plain string so a reason the client does not know yet never fails the whole list.
const B2BStatusReasonSchema = z.string().nullish();

export const B2BOpportunityFilterSchema = z.enum(['all', 'active', 'accepted']);

export const B2BTurnSchema = JobResponseTurnSchema;

export const B2BAvailableActionSchema = JobResponseActionSchema;

export const B2BNegotiationPartySchema = z.enum(['YOU', 'THEM']).nullable();

export const B2BLatestNegotiationSchema = z.object({
  id: z.number().int().positive(),
  version: z.number(),
  numOfFtes: z.number().nullable(),
  pricePerHour: z.string().nullable(),
  totalContractValue: z.string().nullable(),
});

export const B2BNegotiationSummarySchema = z.object({
  version: z.number().nullable(),
  createdAt: z.string().nullable(),
  party: B2BNegotiationPartySchema,
});

export const B2BListingOwnerSchema = z.object({
  displayName: z.string(),
  masked: z.boolean(),
});

export const B2BResponseProviderSchema = z.object({
  responseProviderId: z.number(),
  providerId: z.number().optional(),
  displayName: z.string(),
  masked: z.boolean(),
  providerCategory: z.string().nullable(),
  providerType: z.string().nullable(),
  verificationLevel: z.number().nullable(),
  npiNum: z.string().nullable().optional(),
});

export const B2BResponderSchema = z.object({
  displayName: z.string(),
  masked: z.boolean(),
  employerId: z.number().nullable(),
});

export const B2BUnmaskStateSchema = ResponseUnmaskStateSchema;

export const B2BOpportunityListingSchema = z.object({
  id: z.number(),
  listingTitle: z.string(),
  specialtyName: z.string().nullable(),
  generalSpecialtyFlag: z.boolean().optional(),
  subspecialties: z.array(listingSubspecialtySchema).optional(),
  workLocation: z.string(),
  stateCodes: z.array(z.string()),
  numOfFtes: z.number().nullable(),
  pricePerHour: z.string().nullable(),
  totalContractValue: z.string().nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
});

export const B2BOpportunitySchema = z.object({
  responseId: z.number(),
  status: B2BOpportunityStatusSchema,
  isUnmasked: z.boolean(),
  unmaskState: ResponseUnmaskStateSchema,
  updatedAt: z.string().nullable(),
  appliedAt: z.string().nullable(),
  listing: B2BOpportunityListingSchema,
  listingOwner: B2BListingOwnerSchema,
  latestNegotiation: B2BLatestNegotiationSchema.nullable(),
  negotiationSummaries: z.array(B2BNegotiationSummarySchema).default([]),
  turn: B2BTurnSchema,
  availableActions: JobResponseAvailableActionsSchema,
  responseProviders: z.array(B2BResponseProviderSchema).default([]),
  listingSide: B2BListingSideSchema,
  viewerRole: B2BViewerRoleSchema,
  statusReason: B2BStatusReasonSchema,
});

export const B2BListingResponseSchema = z.object({
  responseId: z.number(),
  status: B2BOpportunityStatusSchema,
  isUnmasked: z.boolean(),
  updatedAt: z.string().nullable(),
  appliedAt: z.string().nullable(),
  listing: B2BOpportunityListingSchema,
  responder: B2BResponderSchema,
  unmaskState: B2BUnmaskStateSchema,
  latestNegotiation: B2BLatestNegotiationSchema.nullable(),
  negotiationSummaries: z.array(B2BNegotiationSummarySchema).default([]),
  turn: B2BTurnSchema,
  availableActions: JobResponseAvailableActionsSchema,
  responseProviders: z.array(B2BResponseProviderSchema).default([]),
  listingSide: B2BListingSideSchema,
  viewerRole: B2BViewerRoleSchema,
  statusReason: B2BStatusReasonSchema,
});

export const B2BMessageSchema = z.object({
  id: z.number(),
  responseId: z.number(),
  messageBody: z.string(),
  senderProviderId: z.number().nullable(),
  senderEmployerId: z.number().nullable(),
  senderUserId: z.number(),
  negotiationId: z.number().nullable(),
  negotiationVersion: z.number().nullable(),
  createdAt: z.string(),
});

/**
 * Full MSaaS negotiation chain row returned by
 * GET /b2b/negotiations/by-response/{responseId}.
 * This is loosely typed — only the fields used by the UI are validated.
 */
export const B2BNegotiationChainRowSchema = z
  .object({
    id: z.number().optional(),
    version: z.number().nullable().optional(),
    senderParty: z.string().nullable().optional(),
    numOfFtes: z.number().nullable().optional(),
    pricePerHour: z.string().nullable().optional(),
    totalContractValue: z.string().nullable().optional(),
    createdAt: z.string().nullable().optional(),
  })
  .passthrough();
